/**
 * HomePage.tsx
 *
 * Main landing page for the Ellucian Marketplace application.
 * Combines the navigation header, hero section, search bar, and main content area.
 * Handles responsive layout, sticky navigation, and search/sort/filter state management.
 *
 * This page is the entry point for users to discover and search for solutions.
 *
 * Production-level comments: No code changes, only documentation for maintainability and clarity.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import { Typography } from '@ellucian/react-design-system/core';
import { NavigationHeaderBar } from '../components/Navigation/NavigationHeaderBar';
import MainContent from '../components/MainContent/MainContent';
import SearchBar from '../components/MainContent/SearchBar';
import CoreIntegrationWidget from '../components/CoreIntegrationWidget';
import { useSelfEntitlement } from '../hooks/useSelfEntitlement';
import { calculateSelfEntitlementCounts } from '../utils/helpers';
import { marketplaceHeroImage, FILTER_STATE_KEY } from '../constants';
import { trackMarketplaceVisit, trackUserLogin } from '../utils/analytics';
import NotchIcon from '../icons/Notch.svg';

function readSavedFilters(): Record<string, any> {
  try {
    const raw = sessionStorage.getItem(FILTER_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * HomePage component
 *
 * Renders the main landing page, including:
 * - Sticky navigation bar
 * - Hero section with background image and headline
 * - Search bar with filter and sort controls
 * - Main content area for displaying solutions
 *
 * Handles responsive layout, sticky nav logic, and search/filter/sort state.
 */
const HomePage: React.FC = () => {

  // Responsive state: track if viewport is mobile or tablet
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Ref for the hero section (used for sticky nav logic)
  const heroSectionRef = useRef<HTMLDivElement>(null);
  // Controls visibility of sticky navigation bar
  const [showStickyNav, setShowStickyNav] = useState(false);
  // Tracks if the page has been scrolled at all (hides the nav divider line)
  const [isPageScrolled, setIsPageScrolled] = useState(false);

  // Search/filter/sort state — lazy-initialised from sessionStorage so filters survive back navigation
  const [appliedSearch, setAppliedSearch] = useState<string>(() => readSavedFilters().appliedSearch ?? ''); // Search term applied
  const [searchQuery, setSearchQuery] = useState<string>(() => readSavedFilters().searchQuery ?? ''); // Current search input
  const [filteredSolutionsCount, setFilteredSolutionsCount] = useState(0); // Number of filtered results
  const [filtersApplied, setFiltersApplied] = useState(false); // Whether filters are active
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => readSavedFilters().selectedCategories ?? []); // Selected filter categories
  const [sortBy, setSortBy] = useState<string>(() => readSavedFilters().sortBy ?? 'a-z'); // Sort order - default to alphabetical
  const [showSortMenu, setShowSortMenu] = useState(false); // Sort dropdown visibility
  const [showCoreEligibleOnly, setShowCoreEligibleOnly] = useState<boolean>(() => readSavedFilters().showCoreEligibleOnly ?? false); // Core integration filter

  // Ref for sort dropdown (to handle outside clicks)
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  // Ref for MainContent section (used to scroll into view)
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Track all solutions loaded from API (needed for Core product counting)
  const [allSolutions, setAllSolutions] = useState<any[]>([]);

  // Persist filter/sort state to sessionStorage so it survives back navigation within the same session
  useEffect(() => {
    try {
      const current = readSavedFilters();
      sessionStorage.setItem(FILTER_STATE_KEY, JSON.stringify({
        ...current,
        appliedSearch,
        searchQuery,
        selectedCategories,
        sortBy,
        showCoreEligibleOnly,
      }));
    } catch { /* non-critical */ }
  }, [appliedSearch, searchQuery, selectedCategories, sortBy, showCoreEligibleOnly]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    if (!showSortMenu) return;
    function handleClickOutside(event: MouseEvent) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSortMenu]);


  // Update responsive state on window resize
  const { useOktaAuth } = require('@okta/okta-react');
  const { authState } = useOktaAuth();
  React.useEffect(() => {
    if (authState?.isAuthenticated) {
      // Track successful login event
      trackUserLogin('okta', true, 'user');
    }
  }, [authState?.isAuthenticated]);

  // Initialize login state from localStorage so the widget and API call are not delayed
  // while Okta is still loading its token on page refresh.
  // NOTE: intentionally NOT syncing from authState.isAuthenticated here — Okta can briefly
  // report isAuthenticated: false during SPA navigation (detail → home) while re-validating
  // the token, which falsely hides the widget. handleLogout() dispatches a storage event for
  // 'loggedIn' so real logouts are reflected immediately via the listener below.
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    () => localStorage.getItem('loggedIn') === 'true'
  );

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'loggedIn') {
        setIsLoggedIn(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Fetch self-entitlement status (erpType only from API)
  const { status: selfEntitlementStatus, loading: selfEntitlementLoading } = useSelfEntitlement(
    isLoggedIn
  );

  // Calculate counts locally from localStorage entitlementsData
  // Pass allSolutions so it counts only Core products that are entitled
  const entitlementCounts = useMemo(() => {
    return calculateSelfEntitlementCounts(selfEntitlementStatus?.erpType, allSolutions);
  }, [selfEntitlementStatus?.erpType, allSolutions]);

  // Track marketplace visits
  useEffect(() => {
    trackMarketplaceVisit();
  }, []);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 767);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth <= 1023);
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);

    return () => {
      window.removeEventListener('resize', checkViewport);
    };
  }, []);


  // Show sticky navigation bar after scrolling past hero section
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = heroSectionRef.current;
      const scrollPosition = window.scrollY;
      setIsPageScrolled(scrollPosition > 0);
      if (heroSection) {
        const heroHeight = heroSection.offsetHeight; // Height of hero section
        const navBarHeight = 80; // Navigation bar height (px)

        if (scrollPosition < heroHeight - navBarHeight) {
          setShowStickyNav(false);
        } else {
          setShowStickyNav(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="app-shell" style={{ background: '#FFF' }}>
      {/* Sticky Navigation Bar - appears after hero section */}
      {showStickyNav && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            background: 'var(--Primary-iris-600, #7100EB)',
            transition: 'background 0.3s ease',
          }}
        >
          {/* Sticky nav bar overlays page after scrolling past hero */}
          <NavigationHeaderBar isAtTop={false} />
        </div>
      )}

      {/* Combined Header and Hero Section */}
      <div
        className="header-hero-container"
        ref={heroSectionRef}
        style={{
          position: 'relative',
          width: '100%',
          backgroundColor: '#7100EB',
          borderBottom: '8px solid #7100EB',
        }}
      >

        {/* Navigation Header - always visible at top of hero section */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'var(--Primary-iris-600, #7100EB)',
            transition: 'background 0.3s ease, border-bottom 0.2s ease',
            borderBottom: isPageScrolled ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          <NavigationHeaderBar />
        </div>

        {/* Hero Section Content: Headline and subheading */}
        <div
          className="hero-section"
          style={{
            display: 'flex',
            flexDirection: (isMobile || isTablet) ? 'column' : 'row',
            alignItems: (isMobile || isTablet) ? 'flex-start' : 'center',
            justifyContent: (isMobile || isTablet) ? 'flex-start' : 'space-between',
            width: '100%',
            padding: isMobile ? '32px 20px 24px' : isTablet ? '36px 40px 36px 40px' : '48px 100px 48px 100px',
            borderBottom: '8px solid #7100EB',
            boxSizing: 'border-box',
            minHeight: isMobile ? '120px' : 'auto',
            position: 'relative',
            zIndex: 3,
            gap: isMobile ? '28px' : isTablet ? '28px' : '40px',
          }}
        >
          {/* Left: Text Content */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              flex: (isMobile || isTablet) ? '0 0 auto' : 0.6,
              width: (isMobile || isTablet) ? '100%' : 'auto',
            }}
          >
            {isMobile ? (
              <>
                <h1 style={{
                  color: 'var(--Neutral-neutral-100, #FFF)',
                  fontFamily: 'Poppins',
                  fontSize: '28px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '110%',
                  margin: '32px 0 0 0',
                  textAlign: 'left',
                  alignSelf: 'stretch',
                }}>
                  Connect with
                  <br />
                  <span style={{
                    color: '#95F4A0',
                    fontFamily: 'Poppins',
                    fontSize: '28px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '110%',
                  }}>
                    Confidence
                  </span>
                </h1>
                <Typography
                  variant="h4"
                  style={{
                    color: 'var(--Neutral-neutral-100, #FFF)',
                    textAlign: 'left',
                    fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '125%', // 17.5px
                    letterSpacing: '0.5px',
                    margin: '8px 0 0 0',
                    width: 'auto',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  Discover solutions that seamlessly integrate with your Ellucian ecosystem.
                </Typography>
              </>
            ) : (
              <>
                <h1
                  style={{
                    color: 'var(--Neutral-neutral-100, #FFF)',
                    fontFamily: 'Poppins',
                    fontSize: isTablet ? '42px' : '62px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '110%',
                    letterSpacing: 0,
                    margin: 0,
                    textAlign: 'left',
                    alignSelf: 'stretch',
                  }}
                >
                  Connect with
                  <br />
                  <span
                    style={{
                      color: '#95F4A0',
                      fontFamily: 'Poppins',
                      fontSize: isTablet ? '42px' : '62px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '110%',
                      letterSpacing: 0,
                    }}
                  >
                    Confidence
                  </span>
                </h1>
                <Typography
                  // variant="h4"
                  style={{
                    color: 'var(--Neutral-neutral-100, #FFF)',
                    textAlign: 'left',
                    fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    fontSize: isTablet ? '16px' : '22px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: '125%',
                    letterSpacing: '0.5px',
                    margin: '8px 0 0 0',
                    width: 'auto',
                    maxWidth: '700px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  Discover solutions that seamlessly<br />integrate with your Ellucian ecosystem.
                </Typography>
              </>
            )}
          </div>

          {/* Right: Core Integration Widget (all screen sizes when logged in) */}
          {isLoggedIn && allSolutions.length > 0 && (
            <div
              style={{
                flex: (isMobile || isTablet) ? '0 0 auto' : 0.4,
                display: 'flex',
                justifyContent: (isMobile || isTablet) ? 'flex-start' : 'flex-end',
                alignItems: 'center',
                paddingRight: '0',
                width: (isMobile || isTablet) ? '100%' : 'auto',
              }}
            >
              <CoreIntegrationWidget
                bannerCount={selfEntitlementStatus?.erpType === 'Banner' ? entitlementCounts.count : 0}
                bannerMax={selfEntitlementStatus?.erpType === 'Banner' ? entitlementCounts.max : 30}
                colleagueCount={selfEntitlementStatus?.erpType === 'Colleague' ? entitlementCounts.count : 0}
                colleagueMax={selfEntitlementStatus?.erpType === 'Colleague' ? entitlementCounts.max : 30}
                erpTypes={selfEntitlementStatus?.erpType ? [selfEntitlementStatus.erpType] : []}
                preferredErp={selfEntitlementStatus?.erpType}
                isLoading={selfEntitlementLoading}
                onViewOptions={() => {
                  // Apply core-eligible-only filter and scroll to marketplace
                  setShowCoreEligibleOnly(true);

                  // Scroll to MainContent with smooth behavior
                  setTimeout(() => {
                    mainContentRef.current?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Search Section - sticky below hero, includes search bar and sort menu */}
      <div
        style={{
          position: 'sticky',
          top: showStickyNav ? '80px' : '0px', // Adjust based on sticky nav presence
          zIndex: 50,
          width: '100%',
          background: '#F7F8FA',
          transition: 'top 0.3s ease',
          marginBottom: isMobile ? 20 : 32 // Add spacing below search section
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
            alignItems: isMobile ? 'stretch' : 'center',
            padding: isMobile ? '20px 20px' : '32px 32px',
            gap: isMobile ? 20 : 24, // Add more gap between search and filter/sidebar
          }}
        >
          {/* Search bar fills available space */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <SearchBar
              appliedSearch={appliedSearch}
              setAppliedSearch={setAppliedSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filteredSolutionsCount={filteredSolutionsCount}
              filtersApplied={filtersApplied}
              onBack={() => {
                setAppliedSearch('');
                setSearchQuery('');
                setFilteredSolutionsCount(0);
                setFiltersApplied(false);
                setSelectedCategories([]); // Clear categories when clearing search
              }}
              onClearAll={() => {
                setAppliedSearch('');
                setSearchQuery('');
                setSelectedCategories([]); // Clear categories when clearing all
              }}
            />
          </div>
          {/* Sort By Section - right aligned, with dropdown menu */}
          <div ref={sortDropdownRef} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginLeft: '24px',
            marginRight: '24px',
            background: 'transparent',
            borderRadius: 0,
            padding: 0,
            position: 'relative',
            zIndex: 20,
            fontFamily: 'Noto Sans, sans-serif',
          }}>
            <span style={{
              fontFamily: '"Noto Sans", sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              color: '#151618',
              lineHeight: '21px',
              marginRight: 4
            }}>Sort by</span>
            {/* Dropdown for sort order */}
            <div style={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}>
              <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  height: '21px',
                }}>
                  {/* Sort by text (not clickable) */}
                  <span style={{
                    fontFamily: '"Noto Sans", sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    color: '#151618',
                    lineHeight: '21px',
                  }}>
                    {sortBy === 'a-z' && 'Alphabetical'}
                    {sortBy === 'recent' && 'Recently Added'}
                  </span>
                  {/* Chevron button toggles sort menu */}
                  <button
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      width: '16px',
                      height: '16px',
                    }}
                    onClick={() => setShowSortMenu(v => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={showSortMenu}
                    aria-label="Sort options menu"
                  >
                    <Icon name={'chevron-down'} style={{ width: 12, height: 12, color: '#151618' }} />
                  </button>
                </div>
              </div>
              {/* Dropdown menu for sort options */}
              {showSortMenu && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: -6, // Right align since this is on the right side
                    width: 160,
                    background: '#FFF',
                    borderRadius: 2,
                    border: '1px solid #B2B3B7',
                    opacity: 1,
                    boxShadow: '0 4px 16px rgba(52, 63, 85, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '1px',
                    zIndex: 1000,
                  }}
                >
                  {/* Notch for dropdown menu */}
                  <img
                    src={NotchIcon}
                    alt="dropdown notch"
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: '16px',
                      transform: 'translateX(60%)',
                      width: 28,
                      height: 6,
                      zIndex: 1001,
                    }}
                  />
                  {/* Sort menu options */}
                  {[
                    { value: 'a-z', label: 'Alphabetical' },
                    { value: 'recent', label: 'Recently Added' }
                  ].map(opt => (
                    <button
                      key={opt.value}
                      role="option"
                      aria-selected={sortBy === opt.value}
                      onClick={() => { setSortBy(opt.value); setShowSortMenu(false); }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '53px',
                        minWidth: '160px',
                        padding: '16px 20px',
                        gap: '8px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                        fontSize: 14,
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '21px',
                        color: 'var(--Neutral-neutral-600, #151618)',
                        letterSpacing: '0',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}
                      tabIndex={0}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area: displays solutions grid and filters */}
      <div
        className="page"
        style={{
          background: '#FFF',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: isMobile ? '24px' : '0px',
          padding: isMobile ? '0 16px' : isTablet ? '0 20px' : '0 32px',
        }}
        ref={mainContentRef}
      >
        <div style={{
          width: '100%',
          maxWidth: '100%'
        }}>
          <MainContent
            appliedSearch={appliedSearch}
            searchQuery={searchQuery}
            sortBy={sortBy}
            erpType={selfEntitlementStatus?.erpType}
            onFilteredSolutionsChange={setFilteredSolutionsCount}
            onFiltersAppliedChange={setFiltersApplied}
            onSolutionsLoaded={setAllSolutions}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            showCoreEligibleOnly={showCoreEligibleOnly}
            setShowCoreEligibleOnly={setShowCoreEligibleOnly}
            onBack={() => {
              setAppliedSearch('');
              setSearchQuery('');
              setFilteredSolutionsCount(0);
              setFiltersApplied(false);
              setSelectedCategories([]);
            }}
            onClearAll={() => {
              setAppliedSearch('');
              setSearchQuery('');
              setSelectedCategories([]);
              setShowCoreEligibleOnly(false);
            }}
            style={{ padding: 0 }}
          />
        </div>
      </div>

      {/* Terms of Service footer */}
      <div
        style={{
          width: '100%',
          padding: isMobile ? '20px 16px' : '20px 32px',
          borderTop: '1px solid #E5E7EB',
          background: '#FFF',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: '#6B7280',
            lineHeight: '21px',
          }}
        >
          All access to and use of the Ellucian Marketplace is governed by the{' '}
          <a
            href="/ellucian-marketplace-terms-of-service.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#026BC8', textDecoration: 'underline' }}
          >
            Ellucian Marketplace Terms of Service
          </a>
        </span>
      </div>
    </div>
  );
}

export default HomePage;